import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Button, Theme, Typography, useMediaQuery } from "@mui/material";
import GestureIcon from "@mui/icons-material/Gesture";
import styled from "@emotion/styled";
import { useChartColors } from "@hooks";
import { useThrottledCallback } from "use-debounce";
import { DrawLineChart } from "../highcharts";
import { CHART_HEIGHT_WITHOUT_NAVIGATOR } from "../highcharts/DrawLineChart/useChartOptions";
import { Figure, ChartLayout, ComparisonValue } from "../BaseChartTools";
import { SecuritiesChartMobileTooltips } from "./SecuritiesChartMobileTooltips";
import { useSecuritiesTooltips } from "./useSecuritiesTooltips";
import {
  SecuritiesChartDataWithDate,
  SecuritiesChartSelectedType,
  SecuritiesChartType,
} from "./types";
import { prepareSecuritiesData } from "./utils/prepareChartData";

const ChartActionsContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const DrawLinesButton = styled(Button)`
  padding: 3px 5px;
  width: max-content;
  height: max-content;
  border-radius: 8px;
`;

export interface SecuritiesChartProps {
  title: string | ReactNode;
  subtitle: ReactNode;
  data: SecuritiesChartDataWithDate[];
  drawLines?: boolean;
  showNavigator?: boolean;
  // Overrides the height a chart without the navigator gets by default.
  heightWithoutNavigator?: number;
  // Phones get the chart's own tooltip next to a tapped point instead of the
  // values row above the chart.
  inlineTooltipOnMobile?: boolean;
  className?: string;
  isFetching?: boolean;
  chartType?: SecuritiesChartType;
  currency?: string;
  comparedTicker?: string | null;
  comparisonValues?: ComparisonValue[];
  selectedTypes?: SecuritiesChartSelectedType[];
  intervalSelector?: ReactNode;
  chartTypeTabs?: ReactNode;
  bottomComponent?: ReactNode;
}

export const SecuritiesChart = ({
  data,
  title,
  subtitle,
  drawLines = false,
  showNavigator = true,
  heightWithoutNavigator = CHART_HEIGHT_WITHOUT_NAVIGATOR,
  inlineTooltipOnMobile = false,
  className,
  isFetching,
  chartType = "performance",
  currency = "EUR",
  comparedTicker,
  comparisonValues = [],
  selectedTypes = ["all"],
  intervalSelector,
  chartTypeTabs,
  bottomComponent,
}: SecuritiesChartProps) => {
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const colors = useChartColors("securitiesChart");
  const [lineDrawingEnabled, setLineDrawingEnabled] = useState(false);
  const [isChartHovering, setIsChartHovering] = useState(false);
  const [selectedX, setSelectedX] = useState<number | undefined>();

  const throttledSetSelectedX = useThrottledCallback(
    (x: number | undefined) => {
      setSelectedX(x);
    },
    300
  );

  const chartColors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(colors).filter(([, value]) => typeof value === "string")
      ) as Record<string, string>,
    [colors]
  );

  const isComparison = Boolean(comparedTicker);

  const currentTicker = comparisonValues.find(
    ({ value }) => value === comparedTicker
  );

  const formattedData = useMemo(
    () =>
      prepareSecuritiesData(
        data,
        [...selectedTypes, ...(isComparison ? ["comparison"] : [])],
        chartColors
      ),
    [data, selectedTypes, isComparison, chartColors]
  );

  const tooltips = useSecuritiesTooltips({
    x: selectedX,
    formattedData,
    currentTicker,
  });

  const handleClickChart = (x?: number) => {
    if (typeof x !== "number") {
      throttledSetSelectedX(undefined);
      return;
    }

    throttledSetSelectedX(x);
  };

  const handleDrawLinesClick = () => {
    setLineDrawingEnabled(!lineDrawingEnabled);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset on prop change
    setLineDrawingEnabled(false);
  }, [chartType]);

  return (
    <ChartLayout
      useCard={false}
      isFetching={isFetching}
      className={className}
      intervalSelector={intervalSelector}
      figure={
        <Figure>
          <Typography variant={isXs ? "h4" : "h2"}>{title}</Typography>
          {subtitle}
        </Figure>
      }
      tooltip={
        isMd && !inlineTooltipOnMobile && !!tooltips ? (
          <SecuritiesChartMobileTooltips data={tooltips} />
        ) : undefined
      }
      onChartMouseEnter={() => {
        setIsChartHovering(true);
      }}
      onChartMouseLeave={() => {
        setIsChartHovering(false);
      }}
      chartTypeTabs={chartTypeTabs}
      bottomComponent={bottomComponent}
    >
      <DrawLineChart
        chartData={formattedData}
        allowDrawing={lineDrawingEnabled}
        chartType={chartType}
        currency={currency}
        comparedTicker={currentTicker}
        onChartClick={handleClickChart}
        showTooltipMobile={inlineTooltipOnMobile}
        showNavigator={showNavigator}
        chartHeight={showNavigator ? undefined : heightWithoutNavigator}
      />
      {(isChartHovering || lineDrawingEnabled) && drawLines && (
        <ChartActionsContainer>
          <DrawLinesButton
            variant="tertiary"
            startIcon={<GestureIcon />}
            size="small"
            onClick={handleDrawLinesClick}
          >
            {lineDrawingEnabled ? "Clear" : "Draw"}
          </DrawLinesButton>
        </ChartActionsContainer>
      )}
    </ChartLayout>
  );
};
